/**
 * Canned / saved replies API.
 * In live inbox mode: calls human-agent-service /api/v1/agent/canned-responses.
 * Otherwise: falls back to in-memory stub.
 */
import { INITIAL_CANNED_RESPONSES } from "@/mocks/cannedResponses";
import { normalizeShortcut } from "@/lib/cannedReplyUtils";
import { getBearerAuthHeaders } from "@/lib/authSession";
import { fetchWithAuthRetry } from "@/lib/authedRequest";
import { isHumanAgentInboxLive, inboxWorkspaceHeaders } from "@/lib/inboxLiveConfig";

const stubDelay = (ms = 80) => new Promise((r) => setTimeout(r, ms));

/** In-memory stand-in used when live mode is off. */
let skeletonStore = [...INITIAL_CANNED_RESPONSES];

async function _cannedFetch(path, { method = "GET", body, workspaceId } = {}) {
  const buildRequest = () => {
    const url = new URL(path, (import.meta.env?.VITE_API_BASE_URL || window.location.origin));
    return fetch(url.toString(), {
      method,
      credentials: "include",
      headers: {
        Accept: "application/json",
        ...getBearerAuthHeaders(),
        ...inboxWorkspaceHeaders(workspaceId),
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  };
  const res = await fetchWithAuthRetry(buildRequest);
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const j = await res.json();
      if (j?.detail) detail = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
      if (j?.error) detail = j.error;
    } catch { /* ignore */ }
    const err = new Error(detail || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  if (res.status === 204) return null;
  return res.json();
}

/**
 * @param {{ workspaceId?: string | null }} [params]
 * @returns {Promise<{ data: Array<{ id: string, shortcut: string, title: string, body: string, visibility: string }> }>}
 */
export async function getCannedResponses(params = {}) {
  if (isHumanAgentInboxLive()) {
    const json = await _cannedFetch("/api/v1/agent/canned-responses", { workspaceId: params.workspaceId });
    return { data: Array.isArray(json?.data) ? json.data : [] };
  }
  await stubDelay();
  return { data: skeletonStore.map((r) => ({ ...r, visibility: "public" })) };
}

/**
 * @param {{ shortcut: string, title: string, body: string, visibility?: "public" | "pvt" | "workspace_wide", workspaceId?: string | null }} payload
 */
export async function postCannedResponse(payload) {
  if (isHumanAgentInboxLive()) {
    const json = await _cannedFetch("/api/v1/agent/canned-responses", {
      method: "POST",
      workspaceId: payload.workspaceId,
      body: {
        shortcut: payload.shortcut,
        title: payload.title,
        body: payload.body,
        visibility: payload.visibility ?? "public",
      },
    });
    return { data: json?.data ?? json };
  }
  await stubDelay();
  const shortcut = normalizeShortcut(payload.shortcut);
  const title = String(payload.title || "").trim();
  const body = String(payload.body || "").trim();
  if (!shortcut) throw new Error("Invalid shortcut.");
  if (!title) throw new Error("Title is required.");
  if (!body) throw new Error("Body is required.");
  if (skeletonStore.some((c) => c.shortcut === shortcut)) {
    throw new Error(`Shortcut /${shortcut} is already used.`);
  }
  const visibility = ["public", "pvt", "workspace_wide"].includes(payload.visibility)
    ? payload.visibility
    : "public";
  const row = { id: `cr-${Date.now()}`, shortcut, title, body, visibility };
  skeletonStore = [...skeletonStore, row];
  return { data: { ...row } };
}

/**
 * @param {{ id: string, workspaceId?: string | null }} params
 */
export async function deleteCannedResponse({ id, workspaceId }) {
  if (isHumanAgentInboxLive()) {
    await _cannedFetch(`/api/v1/agent/canned-responses/${encodeURIComponent(id)}`, {
      method: "DELETE",
      workspaceId,
    });
    return;
  }
  skeletonStore = skeletonStore.filter((r) => r.id !== id);
}

/** Reset store to seed data — useful for tests. */
export function __resetCannedResponsesSkeletonStore() {
  skeletonStore = [...INITIAL_CANNED_RESPONSES];
}
